/**
 * Undo/Redo system types for the MCP command history.
 *
 * Uses inverse-command pattern: each undoable command produces an inverse
 * McpCommand that reverses its effect. No snapshots needed.
 */

import type { McpCommand } from './types'

/** A single entry on the undo or redo stack. */
export interface UndoEntry {
  /** The original command that was executed */
  forward: McpCommand
  /** The inverse command that reverses the original */
  inverse: McpCommand
  /** Timestamp for debounce grouping */
  timestamp: number
  /** Optional transaction group ID */
  transactionId?: string
  /** Grouped entries (for transactions / debounce merges) */
  children?: UndoEntry[]
  /** Forward replay is blocked until a fresh capability confirmation exists. */
  redoPolicy?: 'confirmation-required'
  /** A prior inverse attempt partially succeeded, so replaying this entry
   * would restore only part of the original transaction. */
  discardRedoOnUndo?: boolean
}

/** Snapshot of the undo/redo state. */
export interface UndoState {
  canUndo: boolean
  canRedo: boolean
  stackSize: number
  estimatedBytes: number
  /** Capability plan that owns the current undo-stack top, when applicable. */
  topCapabilityPlanId?: string
}

/** Configuration constants for the undo system. */
export const UNDO_CONFIG = {
  MAX_ENTRIES: 100,
  WARN_THRESHOLD: 0.8, // 80%
  MAX_BYTES: 1_048_576, // 1MB
  DEBOUNCE_MS: 300,
} as const
